import { useEffect, type RefObject } from "react";

// Shared focus trap for overlay surfaces (Modal, Drawer). While `active`, Tab /
// Shift+Tab cycle within `containerRef` instead of escaping to controls behind
// an aria-modal surface — keeping the DOM as inert as the ARIA already claims.
// Only visible (offsetParent !== null) focusables participate.
const FOCUSABLE =
    'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean) {
    useEffect(() => {
        if (!active) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Tab" || !containerRef.current) return;
            const nodes = Array.from(
                containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
            ).filter((n) => n.offsetParent !== null);
            if (nodes.length === 0) {
                e.preventDefault();
                return;
            }
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [containerRef, active]);
}
